// src/utils/largeursEquipements.ts — SCE Issues
// Largeur des colonnes de la liste des équipements, ajustée au contenu (05/10).
//
// Une seule grille pour TOUS les groupes PN (sinon les colonnes ne
// s'aligneraient plus d'un groupe à l'autre), mesurée sur TOUT le référentiel :
// stable quand on filtre.
//
//  - Vocabulaire fermé (N° de série, badge, exécution, OF) : la valeur la plus
//    large, toujours entière — un N° de série coupé ne se lit plus.
//  - Texte libre (modèle, caractéristiques, chef de projet) : la valeur la plus
//    large aussi, plafonnée ; quand la place manque, ces colonnes se serrent
//    jusqu'à un plancher (le texte est alors coupé, complet au survol).
//  - Une colonne vide prend la place qui reste, avant le nombre de problèmes :
//    les colonnes ne s'étirent pas au-delà de ce qu'elles montrent.
//
// Pur : les largeurs de texte sont mesurées ailleurs (canvas) et passées ici.

export interface MesuresEquipements {
  numeroSerie: number
  /** Badge « En fabrication » ; null = pas de colonne. */
  fabrication: number | null
  modele: number
  execution: number
  caracteristiques: number
  of: number
  chef: number
}

/** Plafonds et planchers des colonnes de texte libre. */
export const BORNES_TEXTE_LIBRE = {
  modele: { plancher: 120, plafond: 300 },
  caracteristiques: { plancher: 140, plafond: 440 },
  chef: { plancher: 80, plafond: 200 },
} as const

/** La pastille du nombre de problèmes. */
export const LARGEUR_PROBLEMES = 36

/** Le plus large des textes mesurés, arrondi au pixel supérieur (0 si aucun). */
export function plusLarge(largeurs: readonly number[]): number {
  return Math.ceil(largeurs.reduce((m, l) => Math.max(m, l), 0))
}

function souple(besoin: number, bornes: { plancher: number; plafond: number }): string {
  const max = Math.min(Math.ceil(besoin), bornes.plafond)
  return `minmax(${Math.min(max, bornes.plancher)}px, ${max}px)`
}

/**
 * Gabarit de la grille : N° de série, [badge], modèle, exécution,
 * caractéristiques, OF, chef de projet, place restante, problèmes. Les rangées
 * portent donc une cellule vide avant la pastille.
 */
export function gabaritEquipements(m: MesuresEquipements): string {
  return [
    `${Math.ceil(m.numeroSerie)}px`,
    ...(m.fabrication === null ? [] : [`${Math.ceil(m.fabrication)}px`]),
    souple(m.modele, BORNES_TEXTE_LIBRE.modele),
    `${Math.ceil(m.execution)}px`,
    souple(m.caracteristiques, BORNES_TEXTE_LIBRE.caracteristiques),
    `${Math.ceil(m.of)}px`,
    souple(m.chef, BORNES_TEXTE_LIBRE.chef),
    'minmax(0, 1fr)',
    `${LARGEUR_PROBLEMES}px`,
  ].join(' ')
}
